import { useState, useEffect } from 'react';
import { Search, Users, Send, Check, AlertCircle, Mail, Edit, Trash2, Download } from 'lucide-react';
import { Guest, Invitation, RSVPResponse } from '../types/invitation';
import { rsvpService, exportService } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import PrimaryButton from './themed/PrimaryButton';
import ThemedCard from './themed/ThemedCard';
import { createLogger } from '../lib/logger';

const log = createLogger('invitation');

interface InvitationManagementProps {
  guests: Guest[];
  invitations: Invitation[];
  inviteCode: string; // 新增邀請碼
  onUpdateGuest: (guestId: string, updatedGuest: Partial<Guest>) => void;
  onDeleteGuest: (guestId: string) => void;
  onSendInvitation: (guestIds: string[]) => void;
}

interface GuestWithInvitation extends Guest {
  invitation?: Invitation;
  invitationStatus: 'not_sent' | 'sent' | 'viewed' | 'responded';
  rsvpStatus?: 'yes' | 'no' | 'maybe';
}

export const InvitationManagement: React.FC<InvitationManagementProps> = ({
  guests,
  invitations,
  inviteCode,
  onDeleteGuest,
  onSendInvitation
}) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [selectedGuests, setSelectedGuests] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [rsvpResponses, setRSVPResponses] = useState<RSVPResponse[]>([]);
  const [rsvpStats, setRSVPStats] = useState({
    total: 0,
    attending: 0,
    declined: 0,
    totalGuests: 0
  });
  const [physicalInvitationStats, setPhysicalInvitationStats] = useState({
    totalRequests: 0,
    totalGuests: 0
  });
  const [loadingRSVP, setLoadingRSVP] = useState(true);
  const [exportingData, setExportingData] = useState(false);

  // 合併 guests 和 invitations 資料
  const guestsWithInvitations: GuestWithInvitation[] = guests.map(guest => {
    const invitation = invitations.find(inv => inv.guestId === guest.id);
    let invitationStatus: 'not_sent' | 'sent' | 'viewed' | 'responded' = 'not_sent';
    
    if (invitation) {
      if (invitation.respondedAt) {
        invitationStatus = 'responded';
      } else if (invitation.viewedAt) {
        invitationStatus = 'viewed';
      } else if (invitation.sentAt) {
        invitationStatus = 'sent';
      }
    }

    return {
      ...guest,
      invitation,
      invitationStatus,
      rsvpStatus: invitation?.rsvpResponse?.attending
    };
  });

  // 載入 RSVP 資料
  useEffect(() => {
    const loadRSVPData = async () => {
      if (!user?.id) return;
      
      try {
        setLoadingRSVP(true);
        const [responses, stats, physicalStats] = await Promise.all([
          rsvpService.getRSVPsByInviteCode(inviteCode, user.id),
          rsvpService.getRSVPStats(inviteCode, user.id),
          rsvpService.getPhysicalInvitationStats(inviteCode, user.id)
        ]);
        
        setRSVPResponses(responses);
        setRSVPStats(stats);
        setPhysicalInvitationStats({
          totalRequests: physicalStats.totalRequests,
          totalGuests: physicalStats.totalGuests
        });
      } catch (error) {
        log.error('Error loading RSVP data:', error);
      } finally {
        setLoadingRSVP(false);
      }
    };

    if (inviteCode) {
      loadRSVPData();
    }
  }, [inviteCode]);

  // 計算統計資料 (未使用但保留供未來使用)
  // const stats = {
  //   total: guestsWithInvitations.length,
  //   sent: guestsWithInvitations.filter(g => ['sent', 'viewed', 'responded'].includes(g.invitationStatus)).length,
  //   viewed: guestsWithInvitations.filter(g => ['viewed', 'responded'].includes(g.invitationStatus)).length,
  //   responded: guestsWithInvitations.filter(g => g.invitationStatus === 'responded').length,
  //   attending: guestsWithInvitations.filter(g => g.rsvpStatus === 'yes').length,
  //   declined: guestsWithInvitations.filter(g => g.rsvpStatus === 'no').length,
  //   pending: guestsWithInvitations.filter(g => g.rsvpStatus === 'maybe').length,
  // };

  // 過濾邏輯
  const filteredGuests = guestsWithInvitations.filter(guest => {
    const matchesSearch = guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         guest.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || guest.invitationStatus === filterStatus;
    const matchesGroup = filterGroup === 'all' || guest.group === filterGroup;
    
    return matchesSearch && matchesStatus && matchesGroup;
  });

  // 獲取所有群組
  const allGroups = Array.from(new Set(guests.map(g => g.group)));

  const toggleSelectGuest = (guestId: string) => {
    setSelectedGuests(prev =>
      prev.includes(guestId)
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedGuests.length === filteredGuests.length) {
      setSelectedGuests([]);
    } else {
      setSelectedGuests(filteredGuests.map(g => g.id));
    }
  };

  // 匯出 RSVP 資料
  const handleExportRSVP = async () => {
    if (!user?.id) return;
    
    try {
      setExportingData(true);
      const csvContent = await exportService.exportRSVPToCSV(inviteCode, user.id);
      
      // 建立並下載檔案
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `rsvp-data-${inviteCode}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      URL.revokeObjectURL(link.href);
    } catch (error) {
      log.error('Error exporting RSVP data:', error);
      alert('匯出失敗，請稍後再試');
    } finally {
      setExportingData(false);
    }
  };

  const getStatusBadge = (guest: GuestWithInvitation) => {
    switch (guest.invitationStatus) {
      case 'not_sent':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">未發送</span>;
      case 'sent':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">已發送</span>;
      case 'viewed':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">已查看</span>;
      case 'responded':
        if (guest.rsvpStatus === 'yes') {
          return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">確認出席</span>;
        } else if (guest.rsvpStatus === 'no') {
          return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">無法出席</span>;
        } else {
          return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">考慮中</span>;
        }
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">未知</span>;
    }
  };

  const getRsvpDetails = (guest: GuestWithInvitation) => {
    if (!guest.invitation?.rsvpResponse) return null;
    
    const rsvp = guest.invitation.rsvpResponse;
    return (
      <div className="mt-2 text-sm text-gray-600">
        <div>參加人數: {rsvp.guestCount}</div>
        {rsvp.guestCount > 1 && (
          <div>總參加人數: {rsvp.guestCount} 人</div>
        )}
        {rsvp.message && (
          <div className="mt-1 italic">"{rsvp.message}"</div>
        )}
      </div>
    );
  };

  // 取得實體喜帖狀態
  const getPhysicalInvitationStatus = (guest: GuestWithInvitation) => {
    const rsvp = rsvpResponses.find(r => r.guestEmail === guest.email);
    if (!rsvp) {
      return <span className="text-gray-400 text-sm">-</span>;
    }

    if (rsvp.rsvpData.needsPhysicalInvitation) {
      return (
        <div className="text-sm">
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
            <Mail className="h-3 w-3 mr-1" />
            需要寄送
          </div>
          {rsvp.rsvpData.postalAddress && (
            <div className="text-xs text-gray-600 mt-1 max-w-xs truncate" title={rsvp.rsvpData.postalAddress}>
              {rsvp.rsvpData.postalAddress}
            </div>
          )}
        </div>
      );
    }

    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        不需要
      </span>
    );
  };

  useEffect(() => {
    setShowBulkActions(selectedGuests.length > 0);
  }, [selectedGuests]);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-wedding-text-title mb-2">邀請管理</h1>
        <p className="text-wedding-text-body">管理所有賓客邀請和回覆狀態</p>
      </div>

      {/* 統計卡片 */}
      <ThemedCard className="p-4 bg-gradient-to-r from-wedding-pink/10 to-wedding-gold/10 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center">
            <Users className="h-6 w-6 text-wedding-pink mx-auto mb-2" />
            <div className="text-2xl font-bold text-wedding-pink">{loadingRSVP ? '-' : rsvpStats.total}</div>
            <div className="text-sm text-wedding-text-body">總回覆數</div>
          </div>
          <div className="text-center">
            <Check className="h-6 w-6 text-wedding-pink mx-auto mb-2" />
            <div className="text-2xl font-bold text-wedding-pink">{loadingRSVP ? '-' : rsvpStats.attending}</div>
            <div className="text-sm text-wedding-text-body">確認出席</div>
          </div>
          <div className="text-center">
            <AlertCircle className="h-6 w-6 text-wedding-gold mx-auto mb-2" />
            <div className="text-2xl font-bold text-wedding-gold">{loadingRSVP ? '-' : rsvpStats.declined}</div>
            <div className="text-sm text-wedding-text-body">無法出席</div>
          </div>
          <div className="text-center">
            <Users className="h-6 w-6 text-wedding-pink mx-auto mb-2" />
            <div className="text-2xl font-bold text-wedding-pink">{loadingRSVP ? '-' : rsvpStats.totalGuests}</div>
            <div className="text-sm text-wedding-text-body">總出席人數</div>
          </div>
          <div className="text-center">
            <Mail className="h-6 w-6 text-wedding-gold mx-auto mb-2" />
            <div className="text-2xl font-bold text-wedding-gold">{loadingRSVP ? '-' : physicalInvitationStats.totalRequests}</div>
            <div className="text-sm text-wedding-text-body">需要喜帖</div>
          </div>
          <div className="text-center">
            <button
              onClick={handleExportRSVP}
              disabled={exportingData || loadingRSVP}
              className="w-full flex flex-col items-center justify-center text-wedding-pink hover:text-wedding-pink/80 disabled:text-wedding-text-body/50"
            >
              <Download className="h-6 w-6 mx-auto mb-2" />
              <div className="text-sm font-medium">
                {exportingData ? '匯出中...' : '匯出資料'}
              </div>
            </button>
          </div>
        </div>
      </ThemedCard>

      {/* 搜尋和篩選 */}
      <ThemedCard className="p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-wedding-text-body" />
              <input
                type="text"
                placeholder="搜尋賓客姓名或電子郵件..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-wedding-pink/30 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-wedding-pink/30 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wedding-pink"
            >
              <option value="all">所有狀態</option>
              <option value="not_sent">未發送</option>
              <option value="sent">已發送</option>
              <option value="viewed">已查看</option>
              <option value="responded">已回覆</option>
            </select>
            
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="border border-wedding-pink/30 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wedding-pink"
            >
              <option value="all">所有群組</option>
              {allGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 批量操作 */}
        {showBulkActions && (
          <div className="bg-wedding-pink/10 border border-wedding-pink/30 rounded-md p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-wedding-text-title">
                已選擇 {selectedGuests.length} 位賓客
              </span>
              <div className="flex gap-2">
                <PrimaryButton
                  onClick={() => onSendInvitation(selectedGuests)}
                  className="text-sm flex items-center"
                >
                  <Mail className="h-4 w-4 mr-1" />
                  發送邀請
                </PrimaryButton>
                <PrimaryButton
                  onClick={() => setSelectedGuests([])}
                  variant="outline"
                  className="border-wedding-pink/30 text-wedding-text-body hover:bg-wedding-pink/10 text-sm"
                >
                  取消選擇
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </ThemedCard>

      {/* 賓客列表 */}
      <ThemedCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-wedding-pink/5">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedGuests.length === filteredGuests.length && filteredGuests.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-wedding-pink/30 text-wedding-pink focus:ring-wedding-pink"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-body uppercase tracking-wider">
                  賓客資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-body uppercase tracking-wider">
                  群組/關係
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-body uppercase tracking-wider">
                  邀請狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-body uppercase tracking-wider">
                  回覆詳情
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-body uppercase tracking-wider">
                  實體喜帖
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-body uppercase tracking-wider">
                  電話號碼
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-body uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-transparent divide-y divide-wedding-pink/20">
              {filteredGuests.map((guest) => (
                <tr key={guest.id} className="hover:bg-wedding-pink/5">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedGuests.includes(guest.id)}
                      onChange={() => toggleSelectGuest(guest.id)}
                      className="rounded border-wedding-pink/30 text-wedding-pink focus:ring-wedding-pink"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-wedding-text-title">{guest.name}</div>
                      <div className="text-sm text-wedding-text-body">{guest.email}</div>
                      {guest.phone && (
                        <div className="text-sm text-wedding-text-body">{guest.phone}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-wedding-text-title">{guest.group}</div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(guest)}
                    {guest.invitation?.sentAt && (
                      <div className="text-xs text-wedding-text-body mt-1">
                        發送: {guest.invitation.sentAt.toLocaleDateString()}
                      </div>
                    )}
                    {guest.invitation?.respondedAt && (
                      <div className="text-xs text-wedding-text-body mt-1">
                        回覆: {guest.invitation.respondedAt.toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {getRsvpDetails(guest)}
                  </td>
                  <td className="px-6 py-4">
                    {getPhysicalInvitationStatus(guest)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-wedding-text-title">
                      {rsvpResponses.find(r => r.guestEmail === guest.email)?.rsvpData.contactPhone || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {guest.invitationStatus === 'not_sent' && (
                        <button
                          onClick={() => onSendInvitation([guest.id])}
                          className="text-wedding-pink hover:text-wedding-pink/80"
                          title="發送邀請"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {/* 編輯邏輯 */}}
                        className="text-wedding-text-body hover:text-wedding-gold"
                        title="編輯賓客"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDeleteGuest(guest.id)}
                        className="text-red-600 hover:text-red-700"
                        title="刪除賓客"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredGuests.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-wedding-text-body/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-wedding-text-title mb-2">沒有找到賓客</h3>
            <p className="text-wedding-text-body">調整搜尋條件或篩選器以查看更多結果</p>
          </div>
        )}
      </ThemedCard>

      {/* RSVP 回覆列表 */}
      {rsvpResponses.length > 0 && (
        <ThemedCard className="p-6 mt-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-wedding-text-title">最新 RSVP 回覆</h2>
            <span className="text-sm text-wedding-text-body">共 {rsvpResponses.length} 則回覆</span>
          </div>

          <div className="space-y-4">
            {rsvpResponses.slice(0, 10).map((response) => (
              <ThemedCard key={response.id} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium text-wedding-text-title">{response.guestName}</h3>
                    <p className="text-sm text-wedding-text-body">{response.guestEmail}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      response.rsvpData.attending === 'yes' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {response.rsvpData.attending === 'yes' ? '確認出席' : '無法出席'}
                    </span>
                    <span className="text-xs text-wedding-text-body">
                      {response.submittedAt.toLocaleDateString('zh-TW')}
                    </span>
                  </div>
                </div>

                {response.rsvpData.attending === 'yes' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-wedding-text-body">出席人數：</span>
                      <span className="font-medium text-wedding-text-title">{response.rsvpData.guestCount} 人</span>
                    </div>
                    {response.rsvpData.dietaryRestrictions && response.rsvpData.dietaryRestrictions.length > 0 && (
                      <div>
                        <span className="text-wedding-text-body">特殊需求：</span>
                        <span className="font-medium text-wedding-text-title">
                          {response.rsvpData.dietaryRestrictions.map(dr => 
                            `${dr.id === 'vegetarian' ? '素食' : '兒童椅'} ${dr.count}`
                          ).join(', ')}
                        </span>
                      </div>
                    )}
                    {response.rsvpData.contactPhone && (
                      <div>
                        <span className="text-wedding-text-body">聯絡電話：</span>
                        <span className="font-medium text-wedding-text-title">{response.rsvpData.contactPhone}</span>
                      </div>
                    )}
                  </div>
                )}

                {(response.rsvpData.specialRequests || response.rsvpData.message) && (
                  <div className="mt-3 pt-3 border-t border-wedding-pink/20">
                    <span className="text-wedding-text-body text-sm">留言：</span>
                    <p className="text-sm text-wedding-text-title italic mt-1">
                      "{response.rsvpData.specialRequests || response.rsvpData.message}"
                    </p>
                  </div>
                )}
              </ThemedCard>
            ))}
          </div>

          {rsvpResponses.length > 10 && (
            <div className="text-center mt-4">
              <span className="text-sm text-wedding-text-body">
                顯示最新 10 則回覆，共 {rsvpResponses.length} 則
              </span>
            </div>
          )}
        </ThemedCard>
      )}
    </div>
  );
};